import { Alert, Button, Spin } from 'antd'
import type { UploadFile } from 'antd/es/upload/interface'
import { TrialBalancePanel, type TbSource } from './TrialBalancePanel'
import { WorkpapersPanel } from './WorkpapersPanel'

type UploadFilesStepProps = {
  hasXeroAssets: boolean
  assets: { account: string; balance: number }[]
  indexFileList: UploadFile[]
  onIndexFileListChange: (fileList: UploadFile[]) => void
  workpaperFileList: UploadFile[]
  onWorkpaperFileListChange: (fileList: UploadFile[]) => void
  onStartAnalysis: () => void
  isUploading: boolean
  isLoadingAssets: boolean
  error: string | null
  tbSource: TbSource
  onTbSourceChange: (source: TbSource) => void
  isReuploadMode?: boolean
  reuploadMessage?: string
  disableTrialBalanceControls?: boolean
  uploadMessage?: string
}

export function UploadFilesStep({
  hasXeroAssets,
  assets,
  indexFileList,
  onIndexFileListChange,
  workpaperFileList,
  onWorkpaperFileListChange,
  onStartAnalysis,
  isUploading,
  isLoadingAssets,
  error,
  tbSource,
  onTbSourceChange,
  isReuploadMode = false,
  reuploadMessage,
  disableTrialBalanceControls = false,
  uploadMessage,
}: UploadFilesStepProps) {
  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {error && (
        <div className="mb-4 shrink-0">
          <Alert type="error" showIcon message={error} />
        </div>
      )}
      {isReuploadMode && reuploadMessage && (
        <div className="mb-4 shrink-0">
          <Alert type="info" showIcon message={reuploadMessage} />
        </div>
      )}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 md:grid-cols-2">
        <TrialBalancePanel
          hasXeroAssets={hasXeroAssets}
          assets={assets}
          indexFileList={indexFileList}
          onIndexFileListChange={onIndexFileListChange}
          tbSource={tbSource}
          onTbSourceChange={onTbSourceChange}
          disabled={disableTrialBalanceControls}
        />
        <WorkpapersPanel
          fileList={workpaperFileList}
          onFileListChange={onWorkpaperFileListChange}
        />
      </div>
      <div className="mt-8 flex shrink-0 justify-center">
        <Button
          type="primary"
          size="large"
          loading={isUploading || isLoadingAssets}
          onClick={() => void onStartAnalysis()}
        >
          {isReuploadMode ? 'Re-run AI Analysis →' : 'Start AI Analysis →'}
        </Button>
      </div>
      {isUploading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-white/80 backdrop-blur-[1px]">
          <div className="mx-4 max-w-md rounded-2xl border border-slate-200 bg-white px-6 py-5 text-center shadow-lg">
            <Spin size="large" />
            <div className="mt-4 text-base font-semibold text-slate-800">Upload in progress</div>
            <div className="mt-2 text-sm text-slate-600">
              {uploadMessage ?? 'Uploading files and preparing analysis. Please keep this window open until the next step loads.'}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
